import React, { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';

import { FormattedMessage, useIntl } from '../../util/reactIntl';

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../components/ui/form';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Alert, AlertDescription } from '../../components/ui/alert';

import { NamedLink } from '../../components';

import { createLoginSchema, type LoginFormValues } from './schemas';

interface LoginCardProps {
  onSubmit: (values: LoginFormValues) => void;
  loginError: any | null;
  inProgress: boolean;
}

const LoginCard: React.FC<LoginCardProps> = ({ onSubmit, loginError, inProgress }) => {
  const intl = useIntl();
  const schema = useMemo(() => createLoginSchema(intl), [intl]);

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  return (
    <div className="space-y-6">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  <FormattedMessage id="LoginForm.emailLabel" />
                </FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    placeholder={intl.formatMessage({ id: 'LoginForm.emailPlaceholder' })}
                    autoComplete="email"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  <FormattedMessage id="LoginForm.passwordLabel" />
                </FormLabel>
                <FormControl>
                  <Input
                    type="password"
                    placeholder={intl.formatMessage({ id: 'LoginForm.passwordPlaceholder' })}
                    autoComplete="current-password"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {loginError ? (
            <Alert variant="destructive">
              <AlertDescription>
                <FormattedMessage id="AuthenticationPage.loginFailed" />
              </AlertDescription>
            </Alert>
          ) : null}

          <Button type="submit" className="w-full" disabled={inProgress}>
            {inProgress ? <Loader2 className="animate-spin" /> : null}
            <FormattedMessage id="LoginForm.logIn" />
          </Button>
        </form>
      </Form>

      <div className="text-center text-sm">
        <NamedLink name="PasswordRecoveryPage" className="text-muted-foreground hover:text-primary underline underline-offset-4">
          <FormattedMessage id="PasswordRecoveryPage.forgotPasswordTitle" />
        </NamedLink>
      </div>
    </div>
  );
};

export default LoginCard;
